// src/components/Prospects.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { loginRequest, protectedResources } from "../authConfig";
import useAuthToken from "../hooks/useAuthToken";
import { createApiClient } from "../utils/apiClient";
import { Modal, Button } from "react-bootstrap";
import { useUser } from "../utils/userContext";
import { useTier } from "../utils/tierContext";
import type { ClientInfo, UserInfo, BillingRole, ClientContact, StateInfo, CountyInfo } from "../types/UserTypes";
import type {
  ProjectInfoCreateUpdateModel,
  AssignedUsersToCreateModel,
} from "../types/apiSchemaAliases";
import styles from "./Prospects.module.css";
import Toast from "./Toast";
import SearchableSelect from "./SearchableSelect";
import ApproverList from "./ApproverList";
import FeatureGate from "../utils/featureGate";
import {
  EFFECTIVE_DATED_HISTORY_FEATURE,
  EFFECTIVE_HISTORY_FEATURE_LOADING_MESSAGE,
  getEffectiveHistoryErrorMessage,
  todayYmd,
  withEffectiveStartDateIfAllowed,
} from "./EffectiveDateField";
import SaveEffectiveDateModal from "./SaveEffectiveDateModal";
import HistoricalReportingHistory, {
  HistoryIconButton,
  type HistoryEndpointConfig,
} from "./HistoricalReportingHistory";

type DescriptionRecord = {
  id?: number;
  projectId?: number;
  description?: string;
  countyId?: number | null;
  countyName?: string;
  section?: string;
  block?: string;
  township?: string;
  range?: string;
  abstract?: string;
  needsSave?: boolean;
};
type AssignmentRecord = AssignedUsersToCreateModel & {
  id?: number;
  recordId?: number;
  firstName?: string;
  lastName?: string;
  businessName?: string;
  billingRoleId?: number;
  roleName?: string;
};

const sortBrokersByFirstName = (list: any) => {
  const key = (b: any) =>
    (b?.firstName ?? b?.businessName ?? b?.lastName ?? "")
      .toString()
      .trim()
      .toLowerCase();

  return [...list].sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    if (ka > kb) return 1;
    if (ka < kb) return -1;
    return 0;
  });
};

const emptyDescriptionInput = () => ({
  description: "",
  countyId: null as number | null,
  section: "",
  block: "",
  township: "",
  range: "",
  abstract: "",
});

const toStringValue = (value: unknown, fallback = "") =>
  value === null || value === undefined ? fallback : String(value);

const toNumberValue = (value: unknown, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toNullableNumber = (value: unknown) =>
  value === null || value === undefined || value === "" ? null : toNumberValue(value);

const normalizeLookupUser = (raw: Record<string, unknown>): UserInfo => ({
  ...raw,
  userId: toNumberValue(raw.userId ?? raw.UserId ?? raw.id ?? raw.Id),
  roleId: toNumberValue(raw.roleId ?? raw.RoleId),
  businessName: toStringValue(raw.businessName ?? raw.BusinessName),
  firstName: toStringValue(raw.firstName ?? raw.FirstName),
  lastName: toStringValue(raw.lastName ?? raw.LastName),
  emailAddress: toStringValue(raw.emailAddress ?? raw.EmailAddress),
  companyExpense: Boolean(raw.companyExpense ?? raw.CompanyExpense ?? false),
  isActive: raw.isActive === undefined && raw.IsActive === undefined
    ? true
    : Boolean(raw.isActive ?? raw.IsActive),
});

const normalizeProject = (
  raw: Record<string, unknown>,
  fallback: Partial<ProjectInfoCreateUpdateModel> = {},
): ProjectInfoCreateUpdateModel => ({
  ...fallback,
  ...raw,
  projectId: toNumberValue(raw.projectId ?? raw.ProjectId ?? fallback.projectId),
  clientId: toNumberValue(raw.clientId ?? raw.ClientId ?? fallback.clientId),
  projectName: toStringValue(raw.projectName ?? raw.ProjectName ?? fallback.projectName),
  clientContactId: toNullableNumber(raw.clientContactId ?? raw.ClientContactId ?? fallback.clientContactId),
});

const sortProjectsByName = (list: ProjectInfoCreateUpdateModel[]) =>
  [...list].sort((a, b) => (a.projectName || "").localeCompare(b.projectName || ""));

function PlusIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 4.167v11.666M4.167 10h11.666"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M14.167 2.5a1.768 1.768 0 0 1 2.5 2.5L6.25 15.417l-3.75 1.083 1.083-3.75L14.167 2.5z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SaveIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M4.167 10l4.166 4.167L15.833 5.833"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CancelIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M5 5l10 10M15 5L5 15"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M5.833 6.667v8.75A1.25 1.25 0 0 0 7.083 16.667h5.834a1.25 1.25 0 0 0 1.25-1.25v-8.75M4.167 5h11.666M7.5 5V3.75c0-.69.56-1.25 1.25-1.25h2.5c.69 0 1.25.56 1.25 1.25V5M8.333 9.167v4.166M11.667 9.167v4.166"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function EmptyIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M6.667 2.5h6.666L16.667 5.833V15.833a1.667 1.667 0 0 1-1.667 1.667H5a1.667 1.667 0 0 1-1.667-1.667V4.167A1.667 1.667 0 0 1 5 2.5h1.667ZM10 8.333v5M7.5 10.833h5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const Prospects = () => {
  const authRequest = { ...loginRequest };
  const { token, getToken } = useAuthToken();
  const { user, activeTenantId } = useUser();
  const { hasFeature, loading: tierLoading } = useTier();
  const canUseEffectiveHistory = hasFeature(EFFECTIVE_DATED_HISTORY_FEATURE);

  const api = useMemo(() => {
    if (!token || !activeTenantId || !user?.userId) return null;
    return createApiClient({
      baseUrl: protectedResources.baseURL.endpoint,
      token,
      getToken,
      tenantId: activeTenantId,
      userId: user?.userId,
    });
  }, [token, getToken, activeTenantId, user?.userId]);

  const [clients, setClients] = useState<ClientInfo[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [projects, setProjects] = useState<ProjectInfoCreateUpdateModel[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [brokers, setBrokers] = useState<UserInfo[]>([]);
  const [approverUsers, setApproverUsers] = useState<UserInfo[]>([]);
  const [landmanId, setLandmanId] = useState("");
  const [role, setRole] = useState("Landman");
  const [projectDescriptions, setProjectDescriptions] = useState<DescriptionRecord[]>([]);
  const [descriptionInput, setDescriptionInput] = useState(
    emptyDescriptionInput(),
  );
  const [assignedLandmen, setAssignedLandmen] = useState<AssignmentRecord[]>([]);

  // Billing roles state (system-level)
  const [billingRoles, setBillingRoles] = useState<BillingRole[]>([]);
  const [billingRoleId, setBillingRoleId] = useState<string>("");

  const [selectedClient, setSelectedClient] = useState<ClientInfo | null>(null);
  const [selectedProject, setSelectedProject] = useState<ProjectInfoCreateUpdateModel | null>(null);
  const [clientContacts, setClientContacts] = useState<ClientContact[]>([]);
  const [states, setStates] = useState<StateInfo[]>([]);
  const [counties, setCounties] = useState<CountyInfo[]>([]);
  const [assignedCounties, setAssignedCounties] = useState<any[]>([]);
  const [countyAssignStateId, setCountyAssignStateId] = useState<number | null>(null);
  const [countyAssignCountyId, setCountyAssignCountyId] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingTractIndex, setEditingTractIndex] = useState<number | null>(null);

  const [status, setStatus] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [effectiveDateModal, setEffectiveDateModal] = useState({
    show: false,
    changedFields: [] as string[],
    saving: false,
    error: "",
  });
  const [historyView, setHistoryView] = useState<{
    title: string;
    contextLabel: string;
    configs: HistoryEndpointConfig[];
  } | null>(null);
  const [pendingProjectSave, setPendingProjectSave] = useState<ProjectInfoCreateUpdateModel | null>(null);
  const [pendingDescriptionSave, setPendingDescriptionSave] = useState<any>(null);

  const handleOpenDeleteModal = () => setShowDeleteModal(true);
  const handleCloseDeleteModal = () => setShowDeleteModal(false);

  const showStatus = useCallback((nextStatus: string, message: string) => {
    setStatus(nextStatus);
    setStatusMessage(message);
  }, []);

  const resetLandman = () => {
    setLandmanId("");
    setRole("Landman");
    setAssignedLandmen([]);
  };

  const getBrokerDisplayName = (b: any) => {
    if (!b) return "";
    const full = `${b.firstName || ""} ${b.lastName || ""}`.trim();
    if (full) return full;
    return b.businessName || b.emailAddress || `User ${b.userId}`;
  };

  const openProjectProfileHistory = () => {
    const projectId = Number(selectedProject?.projectId || 0);
    if (!projectId) return;
    setHistoryView({
      title: "Prospect Profile History",
      contextLabel: `${selectedProject?.projectName || `Prospect ${projectId}`} - Prospect Profile`,
      configs: [{
        sectionLabel: "Prospect Profile",
        readPath: `HistoricalReporting/project-profiles/${projectId}`,
        correctionPath: "HistoricalReporting/project-profiles",
        rowScopeFields: ["projectName"],
        rowExcludeFields: ["description", "countyName", "section", "block", "township", "range", "abstract"],
        valueFields: [
          { key: "projectName", label: "Prospect Name" },
        ],
      }],
    });
  };

  const openTractHistory = () => {
    const projectId = Number(selectedProject?.projectId || 0);
    if (!projectId) return;
    setHistoryView({
      title: "Tracts History",
      contextLabel: `${selectedProject?.projectName || `Prospect ${projectId}`} - Tracts`,
      configs: [{
        sectionLabel: "Tracts",
        readPath: `HistoricalReporting/project-profiles/${projectId}`,
        correctionPath: "HistoricalReporting/project-profiles",
        rowScopeFields: ["description", "countyName", "section", "block", "township", "range", "abstract"],
        valueFields: [
          { key: "description", label: "Legal Description" },
          { key: "countyName", label: "County" },
          { key: "section", label: "SEC" },
          { key: "block", label: "BLK" },
          { key: "township", label: "TWN" },
          { key: "range", label: "RNG" },
          { key: "abstract", label: "ABS" },
        ],
      }],
    });
  };

  const openAssignmentHistory = async () => {
    const projectId = Number(selectedProject?.projectId || 0);
    if (!projectId || !api) return;
    let historyAssignments = assignedLandmen;
    if (historyAssignments.length === 0) {
      try {
        const raw = await api.get(`assigneduserstoprojects/${projectId}`);
        const rawAssignments = Array.isArray(raw) ? raw : [];
        historyAssignments = rawAssignments.map((assignment) => {
          const broker: Partial<UserInfo> = brokers.find((b) => b.userId === assignment.userId) || {};
          return {
            ...assignment,
            firstName: broker.firstName || "",
            lastName: broker.lastName || "",
            businessName: broker.businessName || "",
          };
        });
      } catch {
        historyAssignments = [];
      }
    }
    const assignmentsWithUsers = historyAssignments.filter((assignment) => Number(assignment.userId || 0) > 0);
    if (assignmentsWithUsers.length === 0) return;
    setHistoryView({
      title: "Assigned Landmen History",
      contextLabel: `${selectedProject?.projectName || `Prospect ${projectId}`} - Assigned Landmen`,
      configs: assignmentsWithUsers.map((assignment) => ({
        sectionLabel: getBrokerDisplayName(assignment),
        readPath: `HistoricalReporting/project-billing-role-assignments/${projectId}/${Number(assignment.userId)}`,
        correctionPath: "HistoricalReporting/project-billing-role-assignments",
        rowScopeFields: ["billingRoleName", "role"],
        valueFields: [
          { key: "billingRoleName", label: "Billing Role" },
          { key: "role", label: "Role" },
        ],
      })),
    });
  };

  const loadProjects= useCallback(async (clientId: any, { selectFirst = true } = {}) => {
    if (!api) return null;
    setProjectsLoading(true);

    try {
      const data = await api.get(`projectinfo/${clientId}/0`);
      const list = sortProjectsByName(
        (Array.isArray(data) ? data : []).map((item) =>
          normalizeProject(item as Record<string, unknown>, { clientId: toNumberValue(clientId) }),
        ),
      );

      setProjects(list);
      if (selectFirst) {
        if (list.length > 0) {
          setSelectedProject(list[0]);
        } else {
          setSelectedProject({ projectId: 0, clientId, projectName: "" });
        }
      }

      return list;
    } catch (err) {
      console.error("Error loading projects:", err);
      showStatus("error", "Failed to load prospects.");
      return null;
    } finally {
      setProjectsLoading(false);
    }
  }, [api, showStatus]);

  const loadClientContacts = useCallback(async (clientId: any) => {
    if (!api) { setClientContacts([]); return; }
    try {
      const data = await api.get(`clientcontacts/${clientId}`);
      setClientContacts(Array.isArray(data) ? data : []);
    } catch {
      setClientContacts([]);
    }
  }, [api]);

  const loadClients = useCallback(async () => {
    if (!api) return;
    setClientsLoading(true);

    try {
      const data = await api.get("clientinfo/lookup");
      const list = Array.isArray(data) ? data : [];
      list.sort((a, b) =>
        (a.businessName || "").localeCompare(b.businessName || ""),
      );

      setClients(list);

      if (list.length > 0) {
        setSelectedClient(list[0]);
        await loadProjects(list[0].clientId);
        await loadClientContacts(list[0].clientId);
      } else {
        setSelectedClient(null);
        setProjects([]);
        setProjectsLoading(false);
        setSelectedProject(null);
        setClientContacts([]);
      }
    } finally {
      setClientsLoading(false);
      setProjectsLoading(false);
    }
  }, [api, loadProjects, loadClientContacts]);

  const loadProjectDescriptions = useCallback(async (projectId: any) => {
    if (!api) return;

    try {
      const data = await api.get(`projectdescriptions/${projectId}`);
      setProjectDescriptions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("❌ Error loading descriptions:", err);
    }
  }, [api]);

  const loadAssignedCounties = useCallback(async (projectId: any) => {
    if (!api) { setAssignedCounties([]); return; }
    try {
      const data = await api.get(`assignedcountiestoprojects/project/${projectId}`);
      setAssignedCounties(Array.isArray(data) ? data : []);
    } catch {
      setAssignedCounties([]);
    }
  }, [api]);

  const loadBrokers = useCallback(async () => {
    if (!api) return;

    try {
      const [allUsers, nonExpenseUsers] = await Promise.all([
        api.get("userinfo/lookup/users?includeCompanyExpense=true"),
        api.get("userinfo/lookup/users?includeCompanyExpense=false"),
      ]);
      const activeUsers = (Array.isArray(allUsers) ? allUsers : [])
        .map((item) => normalizeLookupUser((item ?? {}) as Record<string, unknown>))
        .filter((item) => item.userId > 0 && item.isActive !== false);
      const activeApprovers = (Array.isArray(nonExpenseUsers) ? nonExpenseUsers : [])
        .map((item) => normalizeLookupUser((item ?? {}) as Record<string, unknown>))
        .filter((item) => item.userId > 0 && item.isActive !== false);

      setBrokers(sortBrokersByFirstName(activeUsers));
      setApproverUsers(sortBrokersByFirstName(activeApprovers));
    } catch (err) {
      console.error("❌ Error loading brokers:", err);
    }
  }, [api]);

  const loadBillingRoles = useCallback(async () => {
    if (!api) { setBillingRoles([]); return; }
    try {
      const data = await api.get<BillingRole[]>("BillingRoles");
      setBillingRoles(Array.isArray(data) ? data.filter((r) => r.isActive) : []);
    } catch {
      setBillingRoles([]);
    }
  }, [api]);

  const loadAssignedLandmen = useCallback(async (projectId: any) => {
    if (!api) return;

    try {
      const raw = await api.get(`assigneduserstoprojects/${projectId}`);
      const rawAssignments = Array.isArray(raw) ? raw : [];

      const enriched = rawAssignments.map((a) => {
        const broker: Partial<UserInfo> = brokers.find((b) => b.userId === a.userId) || {};
        return {
          ...a,
          firstName: broker.firstName || "",
          lastName: broker.lastName || "",
          businessName: broker.businessName || "",
        };
      });

      setAssignedLandmen(enriched);
    } catch (err) {
      console.error("❌ Error loading assigned landmen:", err);
    }
  }, [api, brokers]);

  useEffect(() => {
    if (!api) return;

    (async () => {
      try {
        await Promise.all([
          loadClients(),
          loadBrokers(),
          loadBillingRoles(),
          api.get("stateinfo/").then((d: any) => setStates(Array.isArray(d) ? d : [])),
          api.get("countyinfo/").then((d: any) => setCounties(Array.isArray(d) ? d : [])),
        ]);
      } catch (err: any) {
        console.error(err);
        showStatus("error", err?.message || "Failed to load prospects data.");
      }
    })();
  }, [api, loadClients, loadBrokers, loadBillingRoles, showStatus]);

  const selectedProjectId = selectedProject?.projectId ?? 0;

  useEffect(() => {
    if (selectedProjectId > 0) {
      loadProjectDescriptions(selectedProjectId);
      loadAssignedCounties(selectedProjectId);
    } else {
      setProjectDescriptions([]);
      setAssignedCounties([]);
    }
  }, [selectedProjectId, loadProjectDescriptions, loadAssignedCounties]);

  useEffect(() => {
    if (selectedProjectId > 0) {
      loadAssignedLandmen(selectedProjectId);
    } else {
      setAssignedLandmen([]);
    }
  }, [selectedProjectId, brokers, loadAssignedLandmen]);

  const handleProjectInputChange = (e: any) => {
    const { name, value } = e.target;
    setSelectedProject((prev: any) => ({
      ...(prev || {}),
      [name]: value,
    }));
  };

  const getProjectSchedulingChangedFields = (projectToSave: ProjectInfoCreateUpdateModel) => {
    if (Number(projectToSave.projectId || 0) === 0) return [];
    const original = projects.find((p) => Number(p.projectId) === Number(projectToSave.projectId));
    if (!original) return [];

    const changed: string[] = [];
    if (String(original.projectName || "").trim() !== String(projectToSave.projectName || "").trim()) {
      changed.push("Prospect Name");
    }
    return changed;
  };

  const performProjectSave = async (
    projectToSave: ProjectInfoCreateUpdateModel,
    effectiveStartDate?: string | null,
    fromModal = false,
  ) => {
    if (!api || !selectedProject || !selectedClient) return;
    if (tierLoading) {
      showStatus("error", EFFECTIVE_HISTORY_FEATURE_LOADING_MESSAGE);
      return;
    }

    const isCreate = Number(projectToSave.projectId) === 0;

    const path = isCreate
      ? `projectinfo/${selectedClient.clientId}`
      : `projectinfo/${selectedClient.clientId}/${projectToSave.projectId}`;

    try {
      if (fromModal) {
        setEffectiveDateModal((prev) => ({ ...prev, saving: true, error: "" }));
      }
      // Build clean payload — only API-expected fields
      const payload: Record<string, unknown> = withEffectiveStartDateIfAllowed({
        projectName: projectToSave.projectName,
        clientId: selectedClient.clientId,
        clientContactId: projectToSave.clientContactId || null,
        ...(!isCreate ? { projectId: projectToSave.projectId } : {}),
      }, isCreate ? todayYmd() : effectiveStartDate, canUseEffectiveHistory);

      const saved = isCreate
        ? await api.post<ProjectInfoCreateUpdateModel>(path, payload)
        : await api.put<ProjectInfoCreateUpdateModel>(path, payload);

      const savedProject = normalizeProject((saved || {}) as Record<string, unknown>, projectToSave);
      const savedId = Number(savedProject.projectId);

      setProjects((prev) => {
        const exists = prev.some((p) => Number(p.projectId) === savedId);
        const next = exists
          ? prev.map((p) =>
              Number(p.projectId) === savedId ? { ...p, ...savedProject } : p,
            )
          : [...prev, savedProject];
        return sortProjectsByName(next);
      });

      setSelectedProject(savedProject);

      const fresh = await loadProjects(selectedClient.clientId, {
        selectFirst: false,
      });

      if (fresh) {
        const exact = fresh.find((p) => Number(p.projectId) === savedId);
        if (exact) {
          const selected = normalizeProject(exact as unknown as Record<string, unknown>, savedProject);
          setProjects((prev) =>
            sortProjectsByName(
              prev.map((p) => Number(p.projectId) === savedId ? selected : p),
            ),
          );
          setSelectedProject(selected);
        }
      }

      showStatus(
        "success",
        isCreate
          ? "Prospect created successfully."
          : "Prospect updated successfully.",
      );
      if (fromModal) {
        setEffectiveDateModal({ show: false, changedFields: [], saving: false, error: "" });
        setPendingProjectSave(null);
      }
    } catch (err: any) {
      console.error("❌ Error saving project:", err);
      if (fromModal) {
        setEffectiveDateModal((prev) => ({
          ...prev,
          saving: false,
          error: getEffectiveHistoryErrorMessage(err, err?.message || "Failed to save prospect."),
        }));
      } else {
        showStatus("error", getEffectiveHistoryErrorMessage(err, err?.message || "Failed to save prospect."));
      }
    } finally {
      if (fromModal) {
        setEffectiveDateModal((prev) => (
          prev.show ? { ...prev, saving: false } : prev
        ));
      }
    }
  };

  const saveProject = async (e: any) => {
    e.preventDefault();
    if (!api || !selectedProject || !selectedClient) return;
    if (tierLoading) {
      showStatus("error", EFFECTIVE_HISTORY_FEATURE_LOADING_MESSAGE);
      return;
    }

    const form = e.currentTarget as HTMLFormElement | null;
    const projectNameInput = form?.elements.namedItem("projectName") as HTMLInputElement | null;
    const contactInput = form?.elements.namedItem("clientContactId") as HTMLSelectElement | null;
    const projectToSave = normalizeProject(
      {
        ...selectedProject,
        projectName: projectNameInput?.value ?? selectedProject.projectName,
        clientContactId: contactInput?.value ?? selectedProject.clientContactId ?? null,
      },
      { clientId: selectedClient.clientId },
    );

    const changedFields = getProjectSchedulingChangedFields(projectToSave);
    if (changedFields.length > 0 && canUseEffectiveHistory) {
      setPendingProjectSave(projectToSave);
      setEffectiveDateModal({
        show: true,
        changedFields,
        saving: false,
        error: "",
      });
      return;
    }

    await performProjectSave(projectToSave, null);
  };

  const deleteProject = async () => {
    if (!api || !selectedProject || Number(selectedProject.projectId) === 0)
      return;

    try {
      await api.delete(
        `projectinfo/${selectedProject.clientId}/${selectedProject.projectId}`,
      );

      setProjects((prev) => {
        const next = prev.filter(
          (p) => Number(p.projectId) !== Number(selectedProject.projectId),
        );

        const newSel = next[0] || {
          projectId: 0,
          clientId: selectedClient?.clientId,
        };

        setSelectedProject(newSel);
        return next;
      });

      setProjectDescriptions([]);
      setAssignedLandmen([]);
      resetLandman();
      showStatus("success", "Prospect deleted successfully.");
    } catch (err) {
      console.error("❌ Error deleting project:", err);
      showStatus("error", "Failed to delete prospect.");
    }
  };

  const updateDescription = (index: any, field: any, value: any) => {
    setProjectDescriptions((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
        needsSave: true,
      };
      return updated;
    });
  };

  const saveDescription = async (desc: any, opts?: { inlineError?: boolean }) => {
    if (!api || !selectedProject?.projectId) return;
    if (tierLoading) {
      const err = new Error(EFFECTIVE_HISTORY_FEATURE_LOADING_MESSAGE);
      if (opts?.inlineError) throw err;
      showStatus("error", err.message);
      return;
    }

    const hasId = !!desc.id;
    const path = hasId ? `projectdescriptions/${desc.id}` : "projectdescriptions";
    // Build clean payload — only API-expected fields
    const payload: Record<string, unknown> = withEffectiveStartDateIfAllowed({
      projectId: selectedProject.projectId,
      description: desc.description || null,
      countyId: desc.countyId || null,
      section: desc.section || null,
      block: desc.block || null,
      township: desc.township || null,
      range: desc.range || null,
      abstract: desc.abstract || null,
    }, desc.effectiveStartDate || (hasId ? null : todayYmd()), canUseEffectiveHistory);
    if (hasId) payload.id = desc.id;

    try {
      if (hasId) await api.put(path, payload);
      else await api.post(path, payload);

      await loadProjectDescriptions(selectedProject.projectId);
      setEditingTractIndex(null);
      showStatus("success", hasId ? "Tract updated." : "Tract added.");
    } catch (err) {
      console.error("❌ Error saving description:", err);
      if (opts?.inlineError) throw err;
      showStatus("error", "Failed to save tract.");
    }
  };

  const deleteDescription = async (id: any) => {
    if (!api || !id) return;
    try {
      await api.delete(`projectdescriptions/${id}`);
      await loadProjectDescriptions(selectedProject!.projectId!);
      showStatus("success", "Tract removed.");
    } catch (err) {
      console.error("❌ Error deleting description:", err);
      showStatus("error", "Failed to delete tract.");
    }
  };

  const assignCounty = async () => {
    if (!api || !selectedProject?.projectId || !countyAssignCountyId) return;
    if (assignedCounties.some((ac) => ac.countyId === countyAssignCountyId)) {
      showStatus("error", "County is already assigned.");
      return;
    }
    try {
      await api.post(`assignedcountiestoprojects/project/${selectedProject.projectId}`, {
        countyId: countyAssignCountyId,
      });
      await loadAssignedCounties(selectedProject.projectId);
      setCountyAssignCountyId(null);
      showStatus("success", "County assigned.");
    } catch (err) {
      console.error("❌ Error assigning county:", err);
      showStatus("error", "Failed to assign county.");
    }
  };

  const removeAssignedCounty = async (countyId: number) => {
    if (!api || !selectedProject?.projectId) return;
    try {
      await api.delete(`assignedcountiestoprojects/project/${selectedProject.projectId}/${countyId}`);
      await loadAssignedCounties(selectedProject.projectId);
      showStatus("success", "County removed.");
    } catch (err) {
      console.error("❌ Error removing county:", err);
      showStatus("error", "Failed to remove county.");
    }
  };

  const buildAutoDescription = (input: ReturnType<typeof emptyDescriptionInput>, overrides?: Record<string, any>) => {
    const merged = { ...input, ...overrides };
    const county = assignedCounties.find((ac) => ac.countyId === merged.countyId)?.countyName || "";
    const parts: string[] = [];
    if (merged.section) parts.push(`Sec ${String(merged.section).trim()}`);
    if (merged.block) parts.push(`Blk ${String(merged.block).trim()}`);
    if (merged.township) parts.push(`Twp ${String(merged.township).trim()}`);
    if (merged.range) parts.push(`Rng ${String(merged.range).trim()}`);
    if (merged.abstract) parts.push(`Abs ${String(merged.abstract).trim()}`);
    return parts.length > 0 ? `${county}${county ? " — " : ""}${parts.join(", ")}` : county;
  };

  const handleNewDescriptionInput = (e: any) => {
    const { name, value } = e.target;
    setDescriptionInput((prev) => {
      const next = { ...prev, [name]: value };
      next.description = buildAutoDescription(next);
      return next;
    });
  };

  const addLandman = async () => {
    if (!api || !landmanId || !selectedProject?.projectId) return;
    if (tierLoading) {
      showStatus("error", EFFECTIVE_HISTORY_FEATURE_LOADING_MESSAGE);
      return;
    }

    if (assignedLandmen.some((a) => a.userId === parseInt(landmanId, 10))) {
      showStatus("error", "That user is already assigned.");
      return;
    }

    const payload: Record<string, unknown> = withEffectiveStartDateIfAllowed({
      projectId: selectedProject.projectId,
      userId: parseInt(landmanId, 10),
      role: billingRoles.find((r) => String(r.id) === billingRoleId)?.roleName || role,
    }, todayYmd(), canUseEffectiveHistory);

    if (billingRoleId) {
      payload.billingRoleId = Number(billingRoleId);
    }

    try {
      await api.post("assigneduserstoprojects", payload);
      await loadAssignedLandmen(selectedProject.projectId);
      setLandmanId("");
      setRole("Landman");
      setBillingRoleId("");
      showStatus("success", "Landman assigned successfully.");
    } catch (err) {
      console.error("❌ Error assigning landman:", err);
      showStatus("error", "Failed to assign landman.");
    }
  };

  const removeLandman = async (assignmentId: any) => {
    if (!api) return;
    try {
      await api.delete(`assigneduserstoprojects/${assignmentId}`);
      await loadAssignedLandmen(selectedProject!.projectId!);
      showStatus("success", "Assignment removed.");
    } catch (err) {
      console.error("❌ Error removing landman:", err);
      showStatus("error", "Failed to remove assignment.");
    }
  };

  const canManageTracks = Number(selectedProject?.projectId) > 0;
  const canAssignLandman = Number(selectedProject?.projectId) > 0 && !!landmanId && !!billingRoleId;

  return (
    <>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={authRequest}
      >
        <>
          <div className="main-articles prospects-articles">
            <div className={styles['prospects-shell']}>
              <form onSubmit={saveProject}>
                <div className={styles['prospects-card']} data-testid="prospect-details-card">
                  <div className={styles['prospects-card-header']}>
                    <h2 className={styles['prospects-card-header-title']}>Prospect Details</h2>
                    {canUseEffectiveHistory && Number(selectedProject?.projectId || 0) > 0 && (
                      <HistoryIconButton
                        onClick={openProjectProfileHistory}
                        label={`View effective history for ${selectedProject?.projectName || "prospect"} profile`}
                        testId="project-profile-history"
                      />
                    )}
                  </div>
                  <div className={styles['prospects-card-body']}>
                  <div className={styles['prospects-form-grid']}>
                    <div className={`${styles['prospects-field']} ${styles['prospects-field--half']}`}>
                      <label className={styles['prospects-label']} htmlFor="clientSelect">
                        Select Client
                      </label>
                      <SearchableSelect
                        id="clientSelect"
                        className={styles['prospects-control']}
                        options={clients.map((c) => ({ value: c.clientId, label: c.businessName }))}
                        value={selectedClient?.clientId ?? null}
                        onChange={(val) => {
                          const client = clients.find((c) => c.clientId === val) || null;
                          setSelectedClient(client);
                          setSelectedProject(null);
                          resetLandman();
                          setBillingRoleId("");
                          setClientContacts([]);
                          if (client) {
                            loadProjects(client.clientId);
                            loadClientContacts(client.clientId);
                          } else {
                            setProjects([]);
                          }
                        }}
                        placeholder="Search clients..."
                        disabled={clientsLoading}
                      />
                    </div>

                    <div className={`${styles['prospects-field']} ${styles['prospects-field--half']}`}>
                      <label className={styles['prospects-label']} htmlFor="projectSelect">
                        Select Prospect
                      </label>
                      <SearchableSelect
                        id="projectSelect"
                        className={styles['prospects-control']}
                        options={[
                          { value: 0, label: "New Prospect" },
                          ...projects.map((p) => ({ value: p.projectId ?? 0, label: p.projectName })),
                        ]}
                        value={selectedProject ? (selectedProject.projectId ?? null) : null}
                        onChange={(val) => {
                          const projectId = val ?? 0;
                          resetLandman();
                          if (projectId === 0) {
                            setSelectedProject({ projectId: 0, clientId: selectedClient?.clientId, projectName: "" } as any);
                            return;
                          }
                          const project = projects.find((p) => p.projectId === projectId) || null;
                          setSelectedProject(project);
                        }}
                        placeholder="Search prospects..."
                        disabled={projectsLoading}
                      />
                    </div>

                    <div className={`${styles['prospects-field']} ${styles['prospects-field--third']}`}>
                      <label className={styles['prospects-label']} htmlFor="projectName">
                        Prospect Name
                      </label>
                      <input
                        id="projectName"
                        className={styles['prospects-control']}
                        type="text"
                        name="projectName"
                        placeholder="Prospect Name"
                        value={selectedProject?.projectName || ""}
                        onChange={handleProjectInputChange}
                      />
                    </div>

                    <div className={`${styles['prospects-field']} ${styles['prospects-field--third']}`}>
                      <label className={styles['prospects-label']} htmlFor="clientContactId">
                        Client Contact
                      </label>
                      <select
                        id="clientContactId"
                        className={styles['prospects-control']}
                        value={selectedProject?.clientContactId ?? ""}
                        onChange={(e) => {
                          const val = e.target.value ? Number(e.target.value) : null;
                          setSelectedProject((prev: any) => ({ ...prev, clientContactId: val }));
                        }}
                        disabled={!clientContacts.length}
                      >
                        <option value="">{clientContacts.length ? "Select Contact" : "No contacts — add on Clients page"}</option>
                        {clientContacts.map((c) => (
                          <option key={c.id} value={c.id}>
                            {`${c.firstName || ""} ${c.lastName || ""}`.trim() || "Unnamed"}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className={styles['prospects-form-actions']}>
                      {(selectedProject?.projectId ?? 0) > 0 && (
                        <button
                          type="button"
                          className={styles['prospects-btn-danger']}
                          onClick={handleOpenDeleteModal}
                        >
                          Delete
                        </button>
                      )}

                      <button type="submit" className={styles['prospects-btn']} disabled={tierLoading}>
                        {Number(selectedProject?.projectId || 0) === 0
                          ? "Create Prospect"
                          : "Update"}
                      </button>
                    </div>
                  </div>
                  </div>
                </div>
              </form>

              {/* Assigned Counties Card */}
              {(selectedProject?.projectId ?? 0) > 0 && (
              <div className={styles['prospects-card']} data-testid="counties-card">
                <div className={styles['prospects-card-header']}>
                  <h2 className={styles['prospects-card-header-title']}>Assigned Counties</h2>
                </div>
                <div className={styles['prospects-card-body--flush']}>
                  {canManageTracks && (
                    <div className={`${styles['prospects-composer']} ${styles['prospects-composer--compact']}`}>
                      <div className={styles['prospects-composer__row']}>
                        <div className={`${styles['prospects-composer__field']} ${styles['prospects-composer__field--grow']}`}>
                          <label className={styles['prospects-label']} htmlFor="countyAssignState">State</label>
                          <SearchableSelect
                            id="countyAssignState"
                            className={styles['prospects-control']}
                            options={states.map((s) => ({ value: s.stateId, label: s.name }))}
                            value={countyAssignStateId}
                            onChange={(val) => {
                              setCountyAssignStateId(val);
                              setCountyAssignCountyId(null);
                            }}
                            placeholder="Search states..."
                          />
                        </div>
                        <div className={`${styles['prospects-composer__field']} ${styles['prospects-composer__field--grow']}`}>
                          <label className={styles['prospects-label']} htmlFor="countyAssignCounty">County</label>
                          <SearchableSelect
                            id="countyAssignCounty"
                            className={styles['prospects-control']}
                            options={counties
                              .filter((c) => c.stateId === countyAssignStateId)
                              .map((c) => ({ value: c.countyId, label: c.name }))}
                            value={countyAssignCountyId}
                            onChange={(val) => setCountyAssignCountyId(val)}
                            placeholder={countyAssignStateId ? "Search counties..." : "Select a state first"}
                            disabled={!countyAssignStateId}
                          />
                        </div>
                        <div className={styles['prospects-composer__actions']}>
                          <button
                            type="button"
                            className={`${styles['prospects-btn']} ${styles['prospects-landman-add-btn']}`}
                            onClick={assignCounty}
                            disabled={!countyAssignCountyId}
                          >
                            <PlusIcon />
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className={styles['prospects-card-subheader']}>
                    <span>Assigned Counties</span>
                  </div>

                  {assignedCounties.length === 0 ? (
                    <div className={styles['prospects-empty']}>
                      <div className={styles['prospects-empty__icon']}><EmptyIcon /></div>
                      <p className={styles['prospects-empty__title']}>No counties</p>
                      <p className={styles['prospects-empty__subtitle']}>No counties assigned yet. Add counties to use in tracts.</p>
                    </div>
                  ) : (
                    <>
                      <div className={styles['prospects-landman-list-header']}>
                        <div>County</div>
                        <div>State</div>
                        <div></div>
                      </div>

                      {assignedCounties.map((ac) => {
                        const stateName = states.find((s) => s.stateId === ac.stateId)?.name || "";
                        return (
                          <div className={styles['prospects-landman-row']} key={ac.id} data-testid={`assigned-county-${ac.id}`}>
                            <div className={styles['prospects-landman-row__cell']}>
                              {ac.countyName || `County #${ac.countyId}`}
                            </div>
                            <div className={styles['prospects-landman-row__cell']}>
                              {stateName}
                            </div>
                            <div className={styles['prospects-landman-row__actions']}>
                              {canManageTracks && (
                                <button
                                  type="button"
                                  className={`${styles['prospects-icon-btn']} ${styles['prospects-icon-btn--danger']}`}
                                  onClick={() => removeAssignedCounty(ac.countyId)}
                                  aria-label={`Remove ${ac.countyName || "county"}`}
                                  title="Remove county"
                                  data-testid={`remove-county-${ac.id}`}
                                >
                                  <TrashIcon />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
              )}

              <div className={styles['prospects-card']} data-testid="tracts-card">
                <div className={styles['prospects-card-header']}>
                  <h2 className={styles['prospects-card-header-title']}>Tracts</h2>
                  {canUseEffectiveHistory && Number(selectedProject?.projectId || 0) > 0 && projectDescriptions.length > 0 && (
                    <HistoryIconButton
                      onClick={openTractHistory}
                      label="View effective history for tracts"
                      testId="tracts-history"
                    />
                  )}
                </div>
                <div className={styles['prospects-card-body--flush']}>
                {canManageTracks ? (
                  <>
                    <form onSubmit={(e) => e.preventDefault()}>
                      <div className={styles['prospects-composer']}>
                        <div className={styles['prospects-composer__row']}>
                          <div className={`${styles['prospects-composer__field']} ${styles['prospects-composer__field--grow']}`}>
                            <label className={styles['prospects-label']} htmlFor="newTractCounty">County</label>
                            <select
                              id="newTractCounty"
                              className={styles['prospects-control']}
                              value={descriptionInput.countyId ?? ""}
                              onChange={(e) => {
                                const val = e.target.value ? Number(e.target.value) : null;
                                setDescriptionInput((prev) => {
                                  const next = { ...prev, countyId: val };
                                  next.description = buildAutoDescription(next);
                                  return next;
                                });
                              }}
                              disabled={!assignedCounties.length}
                            >
                              <option value="">{assignedCounties.length ? "Select County" : "Assign counties above"}</option>
                              {assignedCounties.map((ac) => (
                                <option key={ac.countyId} value={ac.countyId}>
                                  {ac.countyName || `County #${ac.countyId}`}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className={`${styles['prospects-composer__field']} ${styles['prospects-composer__field--grow']}`}>
                            <label className={styles['prospects-label']} htmlFor="newTractSection">SEC</label>
                            <input
                              id="newTractSection"
                              className={styles['prospects-control']}
                              type="text"
                              name="section"
                              placeholder="SEC"
                              value={descriptionInput.section}
                              onChange={handleNewDescriptionInput}
                            />
                          </div>

                          <div className={`${styles['prospects-composer__field']} ${styles['prospects-composer__field--grow']}`}>
                            <label className={styles['prospects-label']} htmlFor="newTractBlock">BLK</label>
                            <input
                              id="newTractBlock"
                              className={styles['prospects-control']}
                              type="text"
                              name="block"
                              placeholder="BLK"
                              value={descriptionInput.block}
                              onChange={handleNewDescriptionInput}
                            />
                          </div>

                          <div className={`${styles['prospects-composer__field']} ${styles['prospects-composer__field--grow']}`}>
                            <label className={styles['prospects-label']} htmlFor="newTractTownship">Township</label>
                            <input
                              id="newTractTownship"
                              className={styles['prospects-control']}
                              type="text"
                              name="township"
                              placeholder="Township"
                              value={descriptionInput.township}
                              onChange={handleNewDescriptionInput}
                            />
                          </div>

                          <div className={`${styles['prospects-composer__field']} ${styles['prospects-composer__field--grow']}`}>
                            <label className={styles['prospects-label']} htmlFor="newTractRange">Range</label>
                            <input
                              id="newTractRange"
                              className={styles['prospects-control']}
                              type="text"
                              name="range"
                              placeholder="Range"
                              value={descriptionInput.range}
                              onChange={handleNewDescriptionInput}
                            />
                          </div>

                          <div className={`${styles['prospects-composer__field']} ${styles['prospects-composer__field--grow']}`}>
                            <label className={styles['prospects-label']} htmlFor="newTractAbstract">Abstract</label>
                            <input
                              id="newTractAbstract"
                              className={styles['prospects-control']}
                              type="text"
                              name="abstract"
                              placeholder="Abstract"
                              value={descriptionInput.abstract}
                              onChange={handleNewDescriptionInput}
                            />
                          </div>
                        </div>

                        <div className={styles['prospects-composer__row']}>
                          <div className={`${styles['prospects-composer__field']} ${styles['prospects-composer__field--grow']}`}>
                            <label className={styles['prospects-label']} htmlFor="newTractDescription">Legal Description</label>
                            <input
                              id="newTractDescription"
                              className={styles['prospects-control']}
                              type="text"
                              name="description"
                              placeholder="Auto-generated from fields above"
                              value={descriptionInput.description}
                              onChange={(e) => setDescriptionInput((prev) => ({ ...prev, description: e.target.value }))}
                            />
                          </div>

                          <div className={styles['prospects-composer__actions']}>
                            <button
                              type="button"
                              className={`${styles['prospects-btn']} ${styles['prospects-landman-add-btn']}`}
                              onClick={() => {
                                saveDescription({
                                  ...descriptionInput,
                                  projectId: selectedProject!.projectId,
                                });
                                setDescriptionInput(emptyDescriptionInput());
                              }}
                              disabled={tierLoading}
                            >
                              <PlusIcon />
                              Add Tract
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>

                    <div className={styles['prospects-card-subheader']}>
                      <span>Saved Tracts</span>
                    </div>

                    <div className={styles['prospects-tracts-header']}>
                      <div>Legal Description</div>
                      <div>County</div>
                      <div>SEC</div>
                      <div>BLK</div>
                      <div>TWN</div>
                      <div>RNG</div>
                      <div>ABS</div>
                      <div></div>
                    </div>

                    {projectDescriptions.map((desc, index) => (
                      <div
                        className={`${styles['prospects-tract-row']}${editingTractIndex === index ? ` ${styles['prospects-tract-row--editing']}` : ''}`}
                        key={desc.id || index}
                        data-testid={`tract-row-${desc.id}`}
                      >
                        {editingTractIndex === index ? (
                          <>
                            <input
                              className={styles['prospects-control']}
                              type="text"
                              placeholder="Legal Description"
                              value={desc.description ?? ""}
                              onChange={(e) =>
                                updateDescription(index, "description", e.target.value)
                              }
                              data-testid={`tract-edit-description-${index}`}
                            />
                            <select
                              className={styles['prospects-control']}
                              value={desc.countyId ?? ""}
                              onChange={(e) => {
                                const val = e.target.value ? Number(e.target.value) : null;
                                updateDescription(index, "countyId", val);
                              }}
                              disabled={!assignedCounties.length}
                              data-testid={`tract-edit-county-${index}`}
                            >
                              <option value="">{assignedCounties.length ? "Select County" : "Assign counties first"}</option>
                              {assignedCounties.map((ac) => (
                                <option key={ac.countyId} value={ac.countyId}>
                                  {ac.countyName || `County #${ac.countyId}`}
                                </option>
                              ))}
                            </select>
                            <input
                              className={styles['prospects-control']}
                              type="text"
                              placeholder="SEC"
                              value={desc.section ?? ""}
                              onChange={(e) =>
                                updateDescription(index, "section", e.target.value)
                              }
                              data-testid={`tract-edit-section-${index}`}
                            />
                            <input
                              className={styles['prospects-control']}
                              type="text"
                              placeholder="BLK"
                              value={desc.block ?? ""}
                              onChange={(e) =>
                                updateDescription(index, "block", e.target.value)
                              }
                              data-testid={`tract-edit-block-${index}`}
                            />
                            <input
                              className={styles['prospects-control']}
                              type="text"
                              placeholder="Township"
                              value={desc.township ?? ""}
                              onChange={(e) =>
                                updateDescription(index, "township", e.target.value)
                              }
                              data-testid={`tract-edit-township-${index}`}
                            />
                            <input
                              className={styles['prospects-control']}
                              type="text"
                              placeholder="Range"
                              value={desc.range ?? ""}
                              onChange={(e) =>
                                updateDescription(index, "range", e.target.value)
                              }
                              data-testid={`tract-edit-range-${index}`}
                            />
                            <input
                              className={styles['prospects-control']}
                              type="text"
                              placeholder="Abstract"
                              value={desc.abstract ?? ""}
                              onChange={(e) =>
                                updateDescription(index, "abstract", e.target.value)
                              }
                              data-testid={`tract-edit-abstract-${index}`}
                            />
                            <div className={styles['prospects-tract-actions']}>
                              <button
                                type="button"
                                className={styles['prospects-icon-btn']}
                                onClick={() => {
                                  if (!canUseEffectiveHistory) {
                                    saveDescription(desc);
                                    return;
                                  }
                                  setPendingDescriptionSave(desc);
                                  setEffectiveDateModal({
                                    show: true,
                                    changedFields: ["Tract Details"],
                                    saving: false,
                                    error: "",
                                  });
                                }}
                                disabled={tierLoading || !desc.needsSave}
                                aria-label="Save tract"
                                title="Save tract"
                                data-testid={`tract-save-${desc.id}`}
                              >
                                <SaveIcon />
                              </button>
                              <button
                                type="button"
                                className={styles['prospects-icon-btn']}
                                onClick={() => {
                                  setEditingTractIndex(null);
                                  if (selectedProject?.projectId) {
                                    loadProjectDescriptions(selectedProject.projectId);
                                  }
                                }}
                                aria-label="Cancel editing"
                                title="Cancel"
                              >
                                <CancelIcon />
                              </button>
                              <button
                                type="button"
                                className={`${styles['prospects-icon-btn']} ${styles['prospects-icon-btn--danger']}`}
                                onClick={() => deleteDescription(desc.id)}
                                aria-label="Delete tract"
                                title="Delete tract"
                                data-testid={`tract-delete-${desc.id}`}
                              >
                                <TrashIcon />
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className={styles['prospects-tract-cell']}>{desc.description || "—"}</div>
                            <div className={styles['prospects-tract-cell']}>{desc.countyName || counties.find((c) => c.countyId === desc.countyId)?.name || "—"}</div>
                            <div className={styles['prospects-tract-cell']}>{desc.section || "—"}</div>
                            <div className={styles['prospects-tract-cell']}>{desc.block || "—"}</div>
                            <div className={styles['prospects-tract-cell']}>{desc.township || "—"}</div>
                            <div className={styles['prospects-tract-cell']}>{desc.range || "—"}</div>
                            <div className={styles['prospects-tract-cell']}>{desc.abstract || "—"}</div>
                            <div className={styles['prospects-tract-actions']}>
                              <button
                                type="button"
                                className={styles['prospects-icon-btn']}
                                onClick={() => {
                                  setEditingTractIndex(index);
                                }}
                                aria-label="Edit tract"
                                title="Edit tract"
                                data-testid={`tract-edit-${desc.id}`}
                              >
                                <EditIcon />
                              </button>
                              <button
                                type="button"
                                className={`${styles['prospects-icon-btn']} ${styles['prospects-icon-btn--danger']}`}
                                onClick={() => deleteDescription(desc.id)}
                                aria-label="Delete tract"
                                title="Delete tract"
                                data-testid={`tract-delete-${desc.id}`}
                              >
                                <TrashIcon />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </>
                ) : (
                  <div className={styles['prospects-empty']}>
                    <div className={styles['prospects-empty__icon']}><EmptyIcon /></div>
                    <p className={styles['prospects-empty__title']}>No tracts yet</p>
                    <p className={styles['prospects-empty__subtitle']}>Save the prospect before adding tracts.</p>
                  </div>
                )}
                </div>
              </div>

              <div className={styles['prospects-card']} data-testid="assign-landman-card">
                <div className={styles['prospects-card-header']}>
                  <h2 className={styles['prospects-card-header-title']}>Assign Landman</h2>
                  {canUseEffectiveHistory && Number(selectedProject?.projectId || 0) > 0 && (
                    <HistoryIconButton
                      onClick={openAssignmentHistory}
                      label="View effective history for assigned landmen"
                      testId="assignments-history"
                    />
                  )}
                </div>
                <div className={styles['prospects-card-body']}>

                {Number(selectedProject?.projectId) > 0 ? (
                  <>
                    <form onSubmit={(e) => e.preventDefault()}>
                      <div className={`${styles['prospects-composer']} ${styles['prospects-composer--embedded']}`}>
                        <div className={`${styles['prospects-composer__row']} ${styles['prospects-composer__row--landman']}`}>
                        <div className={styles['prospects-composer__field']}>
                          <label className={styles['prospects-label']} htmlFor="landmanSelect">
                            User
                          </label>
                          <SearchableSelect
                            id="landmanSelect"
                            className={styles['prospects-control']}
                            options={brokers
                              .filter((b) => !assignedLandmen.some((a) => a.userId === b.userId))
                              .map((b) => ({ value: Number(b.userId), label: getBrokerDisplayName(b) }))}
                            value={landmanId ? Number(landmanId) : null}
                            onChange={(val) => setLandmanId(val ? String(val) : "")}
                            placeholder="Search landmen..."
                          />
                        </div>

                        <div className={styles['prospects-composer__field']}>
                          <label className={styles['prospects-label']} htmlFor="roleSelect">
                            Billing Role
                          </label>
                            <SearchableSelect
                              id="roleSelect"
                              className={styles['prospects-control']}
                              options={billingRoles.map((r) => ({ value: r.id, label: r.roleName }))}
                              value={billingRoleId ? Number(billingRoleId) : null}
                              onChange={(val) => setBillingRoleId(val ? String(val) : "")}
                              placeholder="Search roles..."
                            />
                        </div>

                        <div className={styles['prospects-composer__actions']}>
                          <button
                            type="button"
                            className={`${styles['prospects-btn']} ${styles['prospects-landman-add-btn']}`}
                            onClick={addLandman}
                            disabled={tierLoading || !canAssignLandman}
                          >
                            <PlusIcon />
                            Assign User
                          </button>
                        </div>
                        </div>
                      </div>
                    </form>
                  </>
                ) : (
                  <div className={styles['prospects-empty']}>
                    <div className={styles['prospects-empty__icon']}><EmptyIcon /></div>
                    <p className={styles['prospects-empty__title']}>No prospect selected</p>
                    <p className={styles['prospects-empty__subtitle']}>Save the prospect before assigning landmen.</p>
                  </div>
                )}
                </div>

                {Number(selectedProject?.projectId) > 0 && (
                <>
                <div className={styles['prospects-card-subheader']}>
                  <span>Assigned Landmen</span>
                </div>

                <div className={styles['prospects-card-body--flush']}>
                    {assignedLandmen.length === 0 ? (
                      <div className={styles['prospects-empty']}>
                        <div className={styles['prospects-empty__icon']}><EmptyIcon /></div>
                        <p className={styles['prospects-empty__title']}>No assignments</p>
                        <p className={styles['prospects-empty__subtitle']}>No users have been assigned to this prospect yet.</p>
                      </div>
                    ) : (
                      <>
                        <div className={styles['prospects-landman-list-header']}>
                          <div>Name</div>
                          <div>Billing Role</div>
                          <div></div>
                        </div>

                        {assignedLandmen.map((landman, index) => (
                          <div className={styles['prospects-landman-row']} key={landman.id} data-testid={`prospect-landman-${landman.id || index}`}>
                            <div className={styles['prospects-landman-row__cell']}>
                              {getBrokerDisplayName(landman)}
                            </div>

                            <div className={styles['prospects-landman-row__cell']}>
                              {landman.roleName || landman.role}
                            </div>

                            <div className={styles['prospects-landman-row__actions']}>
                              <button
                                type="button"
                                className={`${styles['prospects-icon-btn']} ${styles['prospects-icon-btn--danger']}`}
                                onClick={() => removeLandman(landman.id)}
                                aria-label="Remove assignment"
                                title="Remove assignment"
                                data-testid={`remove-assignment-${landman.id}`}
                              >
                                <TrashIcon />
                              </button>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                </div>
                </>
                )}
              </div>

              <FeatureGate feature="approval_workflow" minLevel="advanced">
                {selectedProject && api && (
                  <div className={styles['prospects-card']} data-testid="approval-workflow-card">
                    <div className={styles['prospects-card-header']}>
                      <h2 className={styles['prospects-card-header-title']}>Approval Workflow</h2>
                    </div>
                    <ApproverList
                        entityType="prospect"
                        entityId={Number(selectedProject.projectId) || 0}
                        users={approverUsers}
                        api={api}
                        hideHeader
                      />
                  </div>
                )}
              </FeatureGate>
            </div>
          </div>

          <Modal
            show={showDeleteModal}
            onHide={handleCloseDeleteModal}
            centered
            dialogClassName={styles['prospects-delete-modal']}
            role="dialog"
            aria-modal="true"
            aria-labelledby="deleteProspectTitle"
            data-testid="delete-prospect-modal"
          >
            <Modal.Header closeButton className="bg-light text-dark">
              <Modal.Title id="deleteProspectTitle">Delete Prospect</Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-light text-dark">
              Are you sure you want to delete this prospect and all associated
              data?
            </Modal.Body>
            <Modal.Footer className="bg-light text-dark">
              <Button variant="secondary" onClick={handleCloseDeleteModal}>
                No
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  deleteProject();
                  handleCloseDeleteModal();
                }}
              >
                Yes, Delete
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      </MsalAuthenticationTemplate>
      {status && (
        <Toast
          message={statusMessage}
          type={status as "success" | "error"}
          onClose={() => { setStatus(null); setStatusMessage(""); }}
        />
      )}
      <SaveEffectiveDateModal
        show={effectiveDateModal.show}
        changedFieldLabels={effectiveDateModal.changedFields}
        saving={effectiveDateModal.saving}
        errorMessage={effectiveDateModal.error}
        onCancel={() => {
          setEffectiveDateModal({ show: false, changedFields: [], saving: false, error: "" });
          setPendingProjectSave(null);
          setPendingDescriptionSave(null);
        }}
        onConfirm={(effectiveStartDate) => {
          if (pendingProjectSave) {
            performProjectSave(pendingProjectSave, effectiveStartDate, true);
            return;
          }
          if (pendingDescriptionSave) {
            setEffectiveDateModal((prev) => ({ ...prev, saving: true, error: "" }));
            saveDescription({ ...pendingDescriptionSave, effectiveStartDate }, { inlineError: true })
              .then(() => {
                setEffectiveDateModal({ show: false, changedFields: [], saving: false, error: "" });
                setPendingDescriptionSave(null);
              })
              .catch((err: any) => {
                setEffectiveDateModal((prev) => ({
                  ...prev,
                  saving: false,
                  error: getEffectiveHistoryErrorMessage(err, err?.message || "Failed to save tract."),
                }));
              });
          }
        }}
      />
      <HistoricalReportingHistory
        show={!!historyView}
        api={api}
        enabled={canUseEffectiveHistory}
        title={historyView?.title || "Effective History"}
        contextLabel={historyView?.contextLabel || ""}
        config={null}
        configs={historyView?.configs || []}
        onClose={() => setHistoryView(null)}
        onChanged={() => {
          if (selectedProject?.projectId) {
            loadProjectDescriptions(selectedProject.projectId);
            loadAssignedLandmen(selectedProject.projectId);
          }
        }}
        onCorrected={(message) => showStatus("success", message)}
      />
    </>
  );
};

export default Prospects;
